import React, { useMemo } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  SortingState,
  PaginationState,
} from '@tanstack/react-table'
import { Button } from './ui/Button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './skeleton/Table'
import { UseQueryResult } from 'react-query'
import { Skeleton } from './skeleton/Skeleton'

interface DataTableProps<TData> {
  columns: Array<ColumnDef<TData, any>>
  fetchData: (args: any) => UseQueryResult<any, unknown>
  initialPageSize?: number
  filters?: Record<string, any>
  defaultSorting?: SortingState
  emptyLabel?: string
}

export function DataTable<TData>({
  columns,
  fetchData,
  initialPageSize = 10,
  filters,
  defaultSorting = [] as SortingState,
  emptyLabel = 'No data found',
}: DataTableProps<TData>): JSX.Element {
  const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  })
  const [sorting, setSorting] = React.useState<SortingState>(() => defaultSorting)

  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize],
  )

  const fetchDataOptions = {
    pageIndex: pageIndex + 1,
    pageSize,
    filters,
    sorting,
  }

  const { data, isPreviousData, isLoading } = fetchData(fetchDataOptions)
  const defaultData = React.useMemo(() => Array(4).fill({}), [])

  const hasData = data?.data?.length > 0
  const tableColumns = useMemo(
    () =>
      isLoading
        ? columns.map((column) => ({
            ...column,
            cell: () => <Skeleton className="w-[100px] h-[20px] rounded-full" />,
          }))
        : columns,
    [columns, isLoading],
  )

  const table = useReactTable({
    data: data?.data ?? defaultData,
    columns: tableColumns,
    state: {
      pagination,
      sorting,
    },
    pageCount: data?.pagination?.lastPage || -1, // Simplified pageCount logic
    getCoreRowModel: getCoreRowModel(),
    debugTable: true,
    manualPagination: true,
    onPaginationChange: setPagination,
    enableSorting: true,
    manualSorting: true,
    onSortingChange: setSorting,
  })

  return (
    <div>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!hasData && !isLoading && <div className=" text-center py-6">{emptyLabel}</div>}
      {hasData && (
        <div className="flex items-center justify-between space-x-2 py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {pageIndex + 1} of {table.getPageCount()}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}>
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
